import { useEffect, useRef, useState } from 'react'

import {
    Box,
    Checkbox,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    Link,
    Radio,
    RadioGroup,
    Show,
    Stack,
    Text,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
    useGetFirstStepVerifyMutation,
    useGetSecondStepVerifyMutation,
    useGetThirdStepVerifyMutation,
    usePostRegisterDataMutation,
    useSendSmsMutation,
} from '@core/app/api/authApi'
import {
    addStepCount,
    allStepsCount,
    cv,
    dateOfBirth,
    email,
    firstName,
    haveDepartments,
    lastName,
    mobilePhone,
    nextStep,
    password,
    removeStepCount,
    repeatEmail,
    serviceId,
    setAddress,
    setCity,
    setCountry,
    setCvBase64,
    setCvExt,
    setCvName,
    setDateOfBirth,
    setFirstName,
    setLastName,
    setMobilePhoneNumber,
    setMobilePhonePrefix,
    setPostalCode,
    setUserAgreement,
    settings,
    stepCount,
    verificationEmailId,
    verificationMobileId,
} from '@core/app/slices/auth'
import type { AuthApi, AuthApiFields } from '@core/app/types/authApiType'
import { AppButton } from '@core/components/AppButton'
import { ApplyJobDropzone } from '@core/components/ApplyJobDropzone'
import { FieldError } from '@core/components/FieldError/FieldError'
import { FlagSelect } from '@core/components/FlagSelect/FlagSelect'
import { DefaultSelect } from '@core/components/Select/Select'
import { SelectGoogleAutocomplete, Values } from '@core/components/SelectGoogleAutocomplete/SelectGoogleAutocomplete'
import { getDaysArray } from '@core/utils/helpers/getDaysArray'
import { getYearsArray } from '@core/utils/helpers/getYearsArray'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'

interface FormValues {
    firstName: string
    lastName: string
    mobilePhone: {
        number: string
        prefix: string
    }
    address: {
        label: string
        address1: string
        city: string
        country: string
        postalCode: string
    }
    dob: dob
    terms?: boolean
    cv: {
        base64: string
        ext: string
        name: string
    } | null
    gender: '0' | '1' | '2' | '3' | '4'
    linkedinUrl: string | null
}

type dob = {
    day: {
        value: string
        label: string
    }
    month: {
        value: string
        label: string
    }
    year: {
        value: string
        label: string
    }
}

interface Props {
    // requiredFields: {
    //     [key: string]: boolean
    // }
    cvParserSettings: AuthApi['cvParserSettings']
    fields?: AuthApiFields
    // requiredSettings: {
    //     [key: string]: boolean
    // }
    gender: '0' | '1' | '2' | '3' | '4'
    onChangeGender: (value: '0' | '1' | '2' | '3' | '4') => void
    linkedinUrl: string | null
    onChangeLinkedinUrl: (value: string | null) => void
}

export const PersonalDataExtendedStep = ({
    fields,
    gender,
    onChangeGender,
    linkedinUrl,
    onChangeLinkedinUrl,
    cvParserSettings,
}: Props) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const stepNumber = useAppSelector(stepCount)
    const stepsCount = useAppSelector(allStepsCount)
    const isDepartmentsExists = useAppSelector(haveDepartments)
    const authSettings = useAppSelector(settings)
    const emailValue = useAppSelector(email)
    const passwordValue = useAppSelector(password)
    const firstNameValue = useAppSelector(firstName)
    const lastNameValue = useAppSelector(lastName)
    const repeatEmailValue = useAppSelector(repeatEmail)
    const cvValue = useAppSelector(cv)
    const dateOfBirthValue = useAppSelector(dateOfBirth)
    const mobilePhoneValue = useAppSelector(mobilePhone)
    const emailId = useAppSelector(verificationEmailId)
    const mobileId = useAppSelector(verificationMobileId)
    const serviceIdValue = useAppSelector(serviceId)

    const [verifyData, { isLoading: isSecondStepLoading }] = useGetSecondStepVerifyMutation()
    const [sendData, { isLoading: isSending }] = usePostRegisterDataMutation()
    const [, { isLoading: isFirstStepLoading }] = useGetFirstStepVerifyMutation()
    const [, { isLoading: isThirdStepLoading }] = useGetThirdStepVerifyMutation()
    const [sendSMS, { isLoading: isSmsLoading }] = useSendSmsMutation()
    const [isLoading, setIsLoading] = useState(false)
    const { executeRecaptcha } = useGoogleReCaptcha()
    const [selectedParseValue, setSelectedParseValue] = useState('yes')

    const genders = [
        {
            value: '1',
            label: t('68'), // Male
        },
        {
            value: '2',
            label: t('69'), // Female
        },
        {
            value: '3',
            label: t('1144'), // Other
        },
        // {
        //     value: '4',
        //     label: t('8888'), // Prefer not to say
        // },
    ]

    const initialValues: FormValues = {
        firstName: firstNameValue,
        lastName: lastNameValue,
        mobilePhone: {
            number: mobilePhoneValue.number ? mobilePhoneValue.number : '',
            prefix: mobilePhoneValue.prefix
                ? mobilePhoneValue.prefix.includes('+')
                    ? mobilePhoneValue.prefix.replace(/\+/g, '')
                    : mobilePhoneValue.prefix
                : '',
        },
        // gender: '0',
        gender: gender,
        cv: cvValue ?? null,
        address: {
            label: '',
            address1: '',
            country: '',
            city: '',
            postalCode: '',
        },
        dob: {
            day: {
                value: dateOfBirthValue ? dateOfBirthValue.split('-')[2] : '',
                label: dateOfBirthValue ? dateOfBirthValue.split('-')[2] : '',
            },
            month: {
                value: dateOfBirthValue ? dateOfBirthValue.split('-')[1] : '',
                label: dateOfBirthValue ? dateOfBirthValue.split('-')[1] : '',
            },
            year: {
                value: dateOfBirthValue ? dateOfBirthValue.split('-')[0] : '',
                label: dateOfBirthValue ? dateOfBirthValue.split('-')[0] : '',
            },
        },
        terms: false,
        linkedinUrl: linkedinUrl,
    }

    const phoneInputRef = useRef<HTMLInputElement | null>(null)
    const firstNameRef = useRef<any>()

    const days = getDaysArray()
    const years = getYearsArray()
    const Months = [
        { value: '01', label: `${t('477')}` },
        { value: '02', label: `${t('478')}` },
        { value: '03', label: `${t('479')}` },
        { value: '04', label: `${t('480')}` },
        { value: '05', label: `${t('481')}` },
        { value: '06', label: `${t('482')}` },
        { value: '07', label: `${t('483')}` },
        { value: '08', label: `${t('484')}` },
        { value: '09', label: `${t('485')}` },
        { value: '10', label: `${t('486')}` },
        { value: '11', label: `${t('487')}` },
        { value: '12', label: `${t('488')}` },
    ]

    const shortMonths = [
        { value: '01', label: `${t('489')}` },
        { value: '02', label: `${t('490')}` },
        { value: '03', label: `${t('491')}` },
        { value: '04', label: `${t('492')}` },
        { value: '05', label: `${t('493')}` },
        { value: '06', label: `${t('494')}` },
        { value: '07', label: `${t('495')}` },
        { value: '08', label: `${t('496')}` },
        { value: '09', label: `${t('497')}` },
        { value: '10', label: `${t('498')}` },
        { value: '11', label: `${t('499')}` },
        { value: '12', label: `${t('500')}` },
    ]

    const formatDateOfBirth = (dob: dob) => {
        if (!dob.day.value.length && !dob.month.value.length && !dob.year.value.length) {
            return undefined
        }
        return `${dob.year.value}-${dob.month.value}-${dob.day.value}`
    }

    const verifyAuthData = async (values: FormValues, submitted: Function, setStatus: Function, setError: Function) => {
        onChangeGender(values.gender)
        values.terms && dispatch(setUserAgreement(values.terms))
        setIsLoading(true)
        try {
            const addressData =
                fields?.address.visible &&
                ((values.address.address1 || '').trim().length > 0 ||
                    (values.address.country || '').trim().length > 0 ||
                    (values.address.city || '').trim().length > 0)
                    ? {
                          address1: values.address.address1,
                          country: values.address.country,
                          city: values.address.city,
                          postalCode: values.address.postalCode,
                      }
                    : null

            const linUrl =
                fields?.linkedinUrl.visible && (values.linkedinUrl || '').trim().length > 0 ? values.linkedinUrl : null

            const response = await verifyData({
                data: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    mobilePhone:
                        fields?.mobilePhone.visible &&
                        ((values.mobilePhone.prefix || '').trim().length > 0 ||
                            String(values.mobilePhone.number || '').trim().length > 0)
                            ? {
                                  prefix: values.mobilePhone.prefix,
                                  number: values.mobilePhone.number,
                              }
                            : undefined,
                    // address: fields?.address.visible ? {
                    //     address1: values.address.address1,
                    //     country: values.address.country,
                    //     city: values.address.city,
                    //     postalCode: values.address.postalCode,
                    // }: undefined,
                    address: addressData,
                    cv: fields?.cv.visible ? values.cv : undefined,
                    dateOfBirth: fields?.dateOfBirth.visible ? formatDateOfBirth(values.dob) : null,
                    userAgreement: values.terms || false,
                    gender: fields?.gender.visible && +values.gender ? +values.gender : undefined,
                    linkedinUrl: linUrl,
                },
            }).unwrap()

            if (response.success) {
                dispatch(setFirstName(values.firstName))
                dispatch(setLastName(values.lastName))
                dispatch(setMobilePhonePrefix(`+${values.mobilePhone.prefix}`))
                dispatch(setMobilePhoneNumber(values.mobilePhone.number))
                dispatch(setAddress(values.address.address1))
                dispatch(setCountry(values.address.country))
                dispatch(setCity(values.address.city))
                if (values.cv?.base64) dispatch(setCvBase64(values.cv?.base64))
                if (values.cv?.ext) dispatch(setCvExt(values.cv?.ext))
                if (values.cv?.name) dispatch(setCvName(values.cv?.name))
                dispatch(setPostalCode(`${values.address.postalCode}`))
                dispatch(setDateOfBirth(formatDateOfBirth(values.dob)))

                // if (authSettings?.validateMobile && requiredFields.mobilePhone) {
                if (
                    authSettings?.validateMobile &&
                    fields?.mobilePhone.visible &&
                    (values.mobilePhone.prefix || '').trim().length > 0 &&
                    String(values.mobilePhone.number || '').trim().length > 0
                ) {
                    sendSMShandler(
                        {
                            prefix: values.mobilePhone.prefix,
                            number: values.mobilePhone.number,
                        },
                        setStatus
                    )
                } else {
                    if (isDepartmentsExists) {
                        dispatch(addStepCount())
                        dispatch(nextStep('departments'))
                    } else {
                        registerHandler(values, setStatus, setError)
                    }
                }
            }
        } catch (error: any) {
            if (error.data?.data?.errors) {
                const errors = error.data.data.errors
                if (errors.firstName) {
                    setError('firstName', errors.firstName[0])
                }
                if (errors.lastName) {
                    setError('lastName', errors.lastName[0])
                }
                if (errors.mobilePhone) {
                    if (errors.mobilePhone.prefix) {
                        setError('mobilePhone.prefix', errors.mobilePhone.prefix[0])
                    }
                    if (errors.mobilePhone.number) {
                        setError('mobilePhone.number', errors.mobilePhone.number[0])
                    }
                }

                if (errors.cv && errors.cv.base64) {
                    setError('cv.base64', errors.cv.base64[0])
                }
                if (errors.cv && errors.cv.ext) {
                    setError('cv.ext', errors.cv.ext[0])
                }
                if (errors.cv && errors.cv.name) {
                    setError('cv.name', errors.cv.name[0])
                }

                if (errors.address) {
                    if (errors.address.address1) {
                        setError('address.address1', errors.address.address1[0])
                    }
                    if (errors.address.country) {
                        setError('address.country', errors.address.country[0])
                    }
                    if (errors.address.city) {
                        setError('address.city', errors.address.city[0])
                    }
                    if (errors.address.postalCode) {
                        setError('address.postalCode', errors.address.postalCode[0])
                    }
                }
                if (errors.dateOfBirth) {
                    setStatus({ dob: errors.dateOfBirth[0] })
                }
                if (errors.terms) {
                    setError('terms', errors.terms[0])
                }
                if (errors.gender) {
                    setError('gender', errors.gender[0])
                }
                if (errors.linkedinUrl) {
                    setError('linkedinUrl', errors.linkedinUrl[0])
                }
            } else {
                console.error('Unexpected error format:', error)
                setStatus({ error: 'An unexpected error occurred. Please try again.' })
            }
        } finally {
            setIsLoading(false)
            submitted()
        }
    }

    const sendSMShandler = async (mobilePhone: { prefix: string; number: string }, setStatus: Function) => {
        if (!executeRecaptcha) {
            console.warn('Recaptcha not loaded')
            return
        }
        const token = await executeRecaptcha('api_phone_send_sms')

        if (token) {
            try {
                await sendSMS({
                    data: {
                        'g-recaptcha-response': token,
                        mobilePhone,
                    },
                }).unwrap()

                dispatch(addStepCount())
                dispatch(nextStep('phoneVerification'))
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage = error?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setStatus({ error: apiMessage })
                } else {
                    console.error('SMS Send Error:', error)
                }
            }
        }
    }

    const registerHandler = async (values: FormValues, setStatus: Function, setError: Function) => {
        if (!executeRecaptcha) {
            console.warn('Recaptcha not loaded')
            return
        }
        const token = await executeRecaptcha('api_register')

        if (token) {
            try {
                setIsLoading(true)
                onChangeGender(values.gender)

                const addressData =
                    fields?.address.visible &&
                    ((values.address.address1 || '').trim().length > 0 ||
                        (values.address.country || '').trim().length > 0 ||
                        (values.address.city || '').trim().length > 0)
                        ? {
                              address1: values.address.address1,
                              country: values.address.country,
                              city: values.address.city,
                              postalCode: values.address.postalCode ? values.address.postalCode : undefined,
                          }
                        : null
                const response = await sendData({
                    data: {
                        validateMobileId: mobileId !== 0 ? mobileId : undefined,
                        validateEmailId: emailId !== 0 ? emailId : undefined,
                        serviceUid: serviceIdValue ? serviceIdValue : undefined,
                        email: emailValue,
                        repeatEmail: repeatEmailValue,
                        cv: fields?.cv.visible ? values.cv : undefined,
                        password: passwordValue,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        userAgreement: values.terms || false,
                        // mobilePhone: {
                        //     prefix: `+${values.mobilePhone.prefix}`,
                        //     number: values.mobilePhone.number.toString(),
                        // },
                        mobilePhone:
                            fields?.mobilePhone.visible &&
                            ((values.mobilePhone.prefix || '').trim().length > 0 ||
                                String(values.mobilePhone.number || '').trim().length > 0)
                                ? {
                                      prefix: `+${values.mobilePhone.prefix}`,
                                      number: values.mobilePhone.number.toString(),
                                  }
                                : undefined,
                        gender: fields?.gender.visible && +values.gender ? +values.gender : null,
                        // address: fields?.address.visible ? {
                        //     address1: values.address.address1,
                        //     country: values.address.country,
                        //     city: values.address.city,
                        //     postalCode: values.address.postalCode ? values.address.postalCode : undefined,
                        // } : null,
                        address: addressData,
                        dateOfBirth: fields?.dateOfBirth.visible ? formatDateOfBirth(values.dob) : null,
                        linkedinUrl:
                            fields?.linkedinUrl.visible && (values.linkedinUrl || '').trim().length > 0
                                ? values.linkedinUrl
                                : null,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                location.href = response.redirectUrl
                localStorage.setItem('is_logged', 'true')
                localStorage.removeItem('apply_dialog')
                localStorage.removeItem('service_code_auth')
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage = error?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setStatus({ error: apiMessage })
                } else if (error.data?.data?.errors) {
                    const errors = error.data.data.errors
                    if (errors.gender) {
                        setError('gender', errors.gender[0])
                    }
                    if (errors.firstName) {
                        setError('firstName', errors.firstName[0])
                    }
                    if (errors.lastName) {
                        setError('lastName', errors.lastName[0])
                    }
                    if (errors.mobilePhone) {
                        if (errors.mobilePhone.prefix) {
                            setError('mobilePhone.prefix', errors.mobilePhone.prefix[0])
                        }
                        if (errors.mobilePhone.number) {
                            setError('mobilePhone.number', errors.mobilePhone.number[0])
                        }
                    }
                    if (errors.cv && errors.cv.base64) {
                        setError('cv.base64', errors.cv.base64[0])
                    }
                    if (errors.cv && errors.cv.ext) {
                        setError('cv.ext', errors.cv.ext[0])
                    }
                    if (errors.cv && errors.cv.name) {
                        setError('cv.name', errors.cv.name[0])
                    }
                    if (errors.address) {
                        if (errors.address.address1) {
                            setError('address.address1', errors.address.address1[0])
                        }
                        if (errors.address.country) {
                            setError('address.country', errors.address.country[0])
                        }
                        if (errors.address.city) {
                            setError('address.city', errors.address.city[0])
                        }
                        if (errors.address.postalCode) {
                            setError('address.postalCode', errors.address.postalCode[0])
                        }
                    }
                    if (errors.dateOfBirth) {
                        setStatus({ dob: errors.dateOfBirth[0] })
                    }
                    if (errors.terms) {
                        setError('terms', errors.terms[0])
                    }
                    if (errors.linkedUrl) {
                        setError('linkedUrl', errors.linkedUrl[0])
                    }
                } else {
                    console.error('Unexpected error format:', error)
                    setStatus({ error: 'An unexpected error occurred. Please try again.' })
                }
            } finally {
                setIsLoading(false)
            }
        }
    }

    useEffect(() => {
        firstNameRef.current.focus()
    }, [])

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} {stepNumber} {t('1165')} {stepsCount}
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'}>
                {t('6604')}
            </Text>
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    firstName: Yup.string().required(`${t('6605')}`),
                    lastName: Yup.string().required(`${t('6606')}`),
                    mobilePhone: Yup.object().when([], {
                        // is: () => requiredFields.mobilePhone,
                        is: () => fields?.mobilePhone.visible && fields?.mobilePhone.mandatory,
                        then: Yup.object().shape({
                            number: Yup.string()
                                .required(`${t('6607')}`)
                                .matches(/^[0-9].{5,12}$/, `${t('4724')}`),
                            prefix: Yup.string()
                                .required(`${t('6609')}`)
                                .min(1, `${t('6609')}`),
                        }),
                        otherwise: Yup.object().notRequired(),
                    }),
                    address: Yup.object().when([], {
                        // is: () => requiredFields.address,
                        is: () => fields?.address.visible && fields?.address.mandatory,
                        then: Yup.object().shape({
                            country: Yup.string().required(`${t('6610')}`),
                        }),
                        otherwise: Yup.object({}).notRequired(),
                    }),
                    dob: Yup.object().when([], {
                        // is: () => requiredFields.dateOfBirth,
                        is: () => fields?.dateOfBirth.visible && fields?.dateOfBirth.mandatory,
                        then: Yup.object().shape({
                            day: Yup.object().shape({
                                value: Yup.string().required(`${t('6613')}`),
                                label: Yup.string().required(`${t('6613')}`),
                            }),
                            month: Yup.object().shape({
                                value: Yup.string().required(`${t('6614')}`),
                                label: Yup.string().required(`${t('6614')}`),
                            }),
                            year: Yup.object().shape({
                                value: Yup.string().required(`${t('6615')}`),
                                label: Yup.string().required(`${t('6615')}`),
                            }),
                        }),
                        otherwise: Yup.object().notRequired(),
                    }),
                    terms: Yup.boolean().when([], {
                        is: () => !isDepartmentsExists,
                        then: Yup.bool().oneOf([true], `${t('6616')}`),
                        otherwise: Yup.boolean().notRequired(),
                    }),
                    cv: Yup.mixed().when([], {
                        // is: () => requiredSettings.cvRequired && requiredFields.cv,
                        is: () => fields?.cv.visible && fields?.cv.mandatory,
                        then: Yup.mixed().required('CV is required'),
                        otherwise: Yup.mixed().notRequired(),
                    }),
                    gender: Yup.string().when([], {
                        // is: () => requiredFields.gender && requiredSettings?.requiredGender,
                        is: () => fields?.gender.visible && fields?.gender.mandatory,
                        // then: Yup.string().required(`${t('584')}`),
                        then: Yup.string().oneOf(['1', '2', '3', '4'], `${t('584')}`),
                        otherwise: Yup.string().notRequired(),
                    }),
                    linkedinUrl: Yup.string().when([], {
                        is: () => fields?.linkedinUrl.visible,
                        then: Yup.string().when([], {
                            is: () => fields?.linkedinUrl.mandatory,
                            then: Yup.string()
                                .required(`${t('9797', 'LinkedIn link is required')}`) // TODO - add id
                                .nullable(true)
                                .url(`${t('9798', 'LinkedIn link is invalid')}`)
                                .matches(/^(https?:\/\/)?(www\.)?(linkedin\.com\/in\/|linkedin\.com\/pub\/)/, {
                                    message: `${t('9798', 'LinkedIn link is invalid')}`,
                                }),
                            otherwise: Yup.string()
                                .notRequired()
                                .nullable(true)
                                .test('is-valid-linkedin', `${t('9798', 'LinkedIn link is invalid')}`, (value) => {
                                    if (!value) return true
                                    const urlRegex = /^(https?:\/\/)?(www\.)?(linkedin\.com\/in\/|linkedin\.com\/pub\/)/
                                    return urlRegex.test(value)
                                }),
                        }),
                        otherwise: Yup.string().notRequired().nullable(true),
                    }),
                })}
                onSubmit={async (values, { setSubmitting, setStatus, setFieldError }) => {
                    const submitted = () => {
                        setSubmitting(false)
                    }
                    if (isDepartmentsExists || authSettings?.validateMobile) {
                        verifyAuthData(values, submitted, setStatus, setFieldError)
                    } else {
                        registerHandler(values, setStatus, setFieldError)
                    }
                }}
            >
                {({
                    values,
                    touched,
                    errors,
                    handleSubmit,
                    handleChange,
                    setFieldValue,
                    handleBlur,
                    status,
                    setStatus,
                    isSubmitting,
                }) => (
                    <Form
                        onSubmit={handleSubmit}
                        style={{
                            width: '100%',
                            marginTop: '24px',
                            marginBottom: '0px',
                        }}
                    >
                        <Flex mb={4} alignItems={'start'} gap={4} flexDirection={{ base: 'column', xl: 'row' }}>
                            <FormControl isInvalid={!!errors.firstName && touched.firstName}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>{t('169')}*</Text>
                                </FormLabel>
                                <Input
                                    ref={firstNameRef}
                                    type={'text'}
                                    name={'firstName'}
                                    value={values.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={`${t('694')}`}
                                    _hover={{
                                        backgroundColor: '#f9f9f9',
                                    }}
                                    _focus={{
                                        boxShadow: '0 0 0 1px #E0E0e0',
                                    }}
                                    cursor={'pointer'}
                                />
                                <FormErrorMessage>
                                    <Text _firstLetter={{ textTransform: 'uppercase' }} textTransform={'lowercase'}>
                                        {errors.firstName && touched.firstName && errors.firstName}
                                    </Text>
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors.lastName && touched.lastName}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>{t('170')}*</Text>
                                </FormLabel>
                                <Input
                                    type={'text'}
                                    name={'lastName'}
                                    value={values.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={`${t('695')}`}
                                    _hover={{
                                        backgroundColor: '#f9f9f9',
                                    }}
                                    _focus={{
                                        boxShadow: '0 0 0 1px #E0E0e0',
                                    }}
                                    cursor={'pointer'}
                                />
                                <FormErrorMessage>
                                    <Text _firstLetter={{ textTransform: 'uppercase' }} textTransform={'lowercase'}>
                                        {errors.lastName && touched.lastName && errors.lastName}
                                    </Text>
                                </FormErrorMessage>
                            </FormControl>
                        </Flex>
                        {fields?.mobilePhone.visible && (
                            <FormControl mb={4}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>
                                        {t('6617')}
                                        {fields?.mobilePhone.mandatory ? '*' : ''}
                                    </Text>
                                </FormLabel>
                                <InputGroup gap={1} isolation={'auto'}>
                                    <FormControl
                                        maxW={28}
                                        isInvalid={!!errors.mobilePhone?.prefix && touched.mobilePhone?.prefix}
                                    >
                                        <FlagSelect
                                            onChange={(value) => {
                                                setFieldValue('mobilePhone.prefix', value)
                                            }}
                                            type={'mobile'}
                                            exist={() => phoneInputRef.current?.focus()}
                                        />
                                        <FormErrorMessage
                                            _firstLetter={{ textTransform: 'uppercase' }}
                                            // textTransform={'lowercase'}
                                        >
                                            {errors.mobilePhone?.prefix && errors.mobilePhone.prefix}
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={!!errors.mobilePhone?.number && touched.mobilePhone?.number}
                                    >
                                        <Input
                                            ref={phoneInputRef}
                                            // type={'number'}
                                            name={'mobilePhone.number'}
                                            value={values.mobilePhone.number}
                                            onChange={(e) => {
                                                const onlyDigits = e.target.value.replace(/\D/g, '')
                                                handleChange({
                                                    target: {
                                                        name: 'mobilePhone.number',
                                                        value: onlyDigits,
                                                    },
                                                })
                                            }}
                                            onBlur={handleBlur}
                                            placeholder={`${t('6618')}`}
                                            _hover={{
                                                backgroundColor: '#f9f9f9',
                                            }}
                                            _focus={{
                                                boxShadow: '0 0 0 1px #E0E0e0',
                                            }}
                                            cursor={'pointer'}
                                        />
                                        <FormErrorMessage>
                                            <Text
                                                _firstLetter={{ textTransform: 'uppercase' }}
                                                // textTransform={'lowercase'}
                                            >
                                                {errors.mobilePhone?.number &&
                                                    touched.mobilePhone?.number &&
                                                    errors.mobilePhone.number}
                                            </Text>
                                        </FormErrorMessage>
                                    </FormControl>
                                </InputGroup>
                            </FormControl>
                        )}
                        {fields?.address.visible && (
                            <FormControl
                                isInvalid={
                                    (!!errors.address?.country && touched.address?.country) ||
                                    (!!errors.address?.city && touched.address?.city)
                                }
                                mb={4}
                            >
                                <FormLabel>
                                    <Text fontSize={'xs'}>
                                        {t('358')}
                                        {fields?.address.mandatory ? '*' : ''}
                                    </Text>
                                </FormLabel>
                                <SelectGoogleAutocomplete
                                    onChange={(value: Values) => setFieldValue('address', value)}
                                    touched={touched.address?.label}
                                    errors={errors.address}
                                />
                                <FormErrorMessage>
                                    <Text _firstLetter={{ textTransform: 'uppercase' }} textTransform={'lowercase'}>
                                        {errors.address?.country && touched.address?.country && errors.address.country}
                                    </Text>
                                </FormErrorMessage>
                                <FormErrorMessage>
                                    <Text _firstLetter={{ textTransform: 'uppercase' }} textTransform={'lowercase'}>
                                        {errors.address?.city && touched.address?.city && errors.address.city}
                                    </Text>
                                </FormErrorMessage>
                            </FormControl>
                        )}
                        {fields?.dateOfBirth.visible && (
                            <FormControl mb={6} w={'full'}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>
                                        {t('6619')}
                                        {fields?.dateOfBirth.mandatory ? '*' : ''}
                                    </Text>
                                </FormLabel>
                                <InputGroup
                                    display={'flex'}
                                    justifyContent={'space-between'}
                                    gap={1}
                                    w={'full'}
                                    isolation={'auto'}
                                >
                                    <FormControl
                                        isInvalid={!!errors.dob?.day?.value && touched.dob?.day?.value}
                                        w={{ base: '30%', xl: '26%' }}
                                    >
                                        <DefaultSelect
                                            removeRightPadding={true}
                                            options={days}
                                            placeholder={'Day'}
                                            className={'w-full'}
                                            values={values.dob.day.value}
                                            onChange={(value) => {
                                                setFieldValue('dob.day', value)
                                                setStatus({
                                                    dob: '',
                                                })
                                            }}
                                        />
                                        <FormErrorMessage>
                                            <Text
                                                _firstLetter={{ textTransform: 'uppercase' }}
                                                textTransform={'lowercase'}
                                            >
                                                {errors.dob?.day?.value &&
                                                    touched.dob?.day?.value &&
                                                    errors.dob.day.value}
                                            </Text>
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={!!errors.dob?.month?.value && touched.dob?.month?.value}
                                        w={{ base: '33%', xl: '44%' }}
                                    >
                                        <Show above={'xl'}>
                                            <DefaultSelect
                                                options={Months}
                                                placeholder={`${t('931')}`}
                                                className={'w-full'}
                                                values={values.dob.month.value}
                                                onChange={(value) => {
                                                    setFieldValue('dob.month', value)
                                                    setStatus({
                                                        dob: '',
                                                    })
                                                }}
                                            />
                                        </Show>
                                        <Show below={'xl'}>
                                            <DefaultSelect
                                                removeRightPadding={true}
                                                options={shortMonths}
                                                placeholder={`${t('931')}`}
                                                className={'w-full'}
                                                values={values.dob.month.value}
                                                onChange={(value) => {
                                                    setFieldValue('dob.month', value)
                                                    setStatus({
                                                        dob: '',
                                                    })
                                                }}
                                            />
                                        </Show>

                                        <FormErrorMessage>
                                            <Text
                                                _firstLetter={{ textTransform: 'uppercase' }}
                                                textTransform={'lowercase'}
                                            >
                                                {errors.dob?.month?.value &&
                                                    touched.dob?.month?.value &&
                                                    errors.dob.month.value}
                                            </Text>
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={!!errors.dob?.year?.value && touched.dob?.year?.value}
                                        w={{ base: '37%', xl: '30%' }}
                                    >
                                        <DefaultSelect
                                            removeRightPadding={true}
                                            options={years}
                                            placeholder={`${t('1322')}`}
                                            className={'w-full'}
                                            values={values.dob.year.value}
                                            onChange={(value) => {
                                                setFieldValue('dob.year', value)
                                                setStatus({
                                                    dob: '',
                                                })
                                            }}
                                        />
                                        <FormErrorMessage>
                                            <Text
                                                _firstLetter={{ textTransform: 'uppercase' }}
                                                textTransform={'lowercase'}
                                            >
                                                {errors.dob?.year?.value &&
                                                    touched.dob?.year?.value &&
                                                    errors.dob.year.value}
                                            </Text>
                                        </FormErrorMessage>
                                    </FormControl>
                                </InputGroup>
                                {status && status.dob ? <FieldError text={status.dob} /> : null}
                            </FormControl>
                        )}
                        {fields?.linkedinUrl.visible && (
                            <FormControl mb={6} w={'full'} isInvalid={!!errors.linkedinUrl && touched.linkedinUrl}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>
                                        {t('9691', 'LinkedIn profile link')}
                                        {fields?.linkedinUrl.mandatory && '*'}
                                    </Text>
                                </FormLabel>
                                <Input
                                    type={'text'}
                                    name={'linkedinUrl'}
                                    value={values.linkedinUrl || ''}
                                    onChange={(val) => {
                                        setFieldValue('linkedinUrl', val.target.value)
                                        onChangeLinkedinUrl(val.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    placeholder={`https://www.linkedin.com/in/username`}
                                    _hover={{
                                        backgroundColor: '#f9f9f9',
                                    }}
                                    _focus={{
                                        boxShadow: '0 0 0 1px #E0E0e0',
                                    }}
                                    cursor={'pointer'}
                                />
                                <FormErrorMessage>
                                    <Text _firstLetter={{ textTransform: 'uppercase' }} textTransform={'lowercase'}>
                                        {errors.linkedinUrl && touched.linkedinUrl && errors.linkedinUrl}
                                    </Text>
                                </FormErrorMessage>
                            </FormControl>
                        )}
                        {fields?.gender.visible && (
                            <FormControl mb={4} isInvalid={!!errors.gender && touched.gender}>
                                <FormLabel>
                                    <Text fontSize={'xs'}>
                                        {t('66')}
                                        {fields?.gender.mandatory ? '*' : ''}
                                    </Text>
                                </FormLabel>
                                <DefaultSelect
                                    placeholder={t('8887') as string}
                                    className={'w-full'}
                                    removeRightPadding={true}
                                    options={genders}
                                    onChange={(value: { value: string; label: string } | any) => {
                                        onChangeGender(value.value as '0' | '1' | '2' | '3' | '4')
                                        setFieldValue('gender', value.value)
                                    }}
                                    values={`$values.gender}`}
                                />
                                <FormErrorMessage>{errors.gender && touched.gender && errors.gender}</FormErrorMessage>
                            </FormControl>
                        )}
                        {fields?.cv.visible && (
                            <FormControl pb="20px">
                                <ApplyJobDropzone
                                    onUpload={(value) => {
                                        setFieldValue('cv', value[0])
                                        if (cvParserSettings?.isAvailable) {
                                            localStorage.setItem('shouldParseCV', 'true')
                                        }
                                    }}
                                    onUploadCv={(value) => {
                                        const { base64, ext, name } = value[0]
                                        setFieldValue('cv.base64', base64)
                                        setFieldValue('cv.ext', ext)
                                        setFieldValue('cv.name', name)
                                    }}
                                    label={`${t('4836')} ${fields?.cv.mandatory ? '*' : ''}`}
                                    description={`${t('7120')}`}
                                    accept={{
                                        'application/pdf': [],
                                        'application/msword': [],
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
                                    }}
                                    maxFiles={1}
                                    maxSize={10485760}
                                    height={'88px'}
                                    applyFiles={values.cv}
                                    type={'cv'}
                                    onApplyFilesDelete={() => setFieldValue('cv', null)}
                                    errorString={
                                        fields?.cv.mandatory && touched.cv && typeof errors.cv === 'string'
                                            ? errors.cv
                                            : undefined
                                    }
                                />
                            </FormControl>
                        )}
                        {values?.cv && cvParserSettings?.isAvailable && (
                            <Box px={'16px'} pb={'20px'}>
                                <Text fontSize="14px" fontWeight="bold">
                                    {t('8799')}
                                </Text>
                                <Text fontSize="12px" pt={'8px'}>
                                    {t('8800')}
                                </Text>
                                <RadioGroup
                                    name="parseCV"
                                    pt={'12px'}
                                    onChange={(value) => {
                                        setSelectedParseValue(value)
                                        if (value === 'no') {
                                            localStorage.setItem('shouldParseCV', 'false')
                                        } else {
                                            localStorage.setItem('shouldParseCV', 'true')
                                        }
                                    }}
                                    value={selectedParseValue}
                                >
                                    <Stack direction="row" spacing={4}>
                                        <Radio value="yes">
                                            <Text fontSize="12px">{t('128')}</Text>
                                        </Radio>
                                        <Radio value="no">
                                            <Text fontSize="12px">{t('8801')}</Text>
                                        </Radio>
                                    </Stack>
                                </RadioGroup>
                            </Box>
                        )}
                        {!isDepartmentsExists ? (
                            <FormControl isInvalid={!!errors.terms && touched.terms} mb={8}>
                                <Checkbox
                                    checked={values.terms}
                                    onChange={(e) => setFieldValue('terms', e.target.checked)}
                                    alignItems={'start'}
                                >
                                    <Text fontSize={'xs'}>
                                        {t('6620')}
                                        <Link
                                            target={'_blank'}
                                            href={'/user-agreement'}
                                            variant={'link'}
                                            fontSize={'xs'}
                                            color={'text.link'}
                                            fontWeight={'medium'}
                                            ml={0.5}
                                        >
                                            {t('4635')}
                                        </Link>
                                        ,{' '}
                                        <Link
                                            target={'_blank'}
                                            href={'/privacy-policy'}
                                            variant={'link'}
                                            fontSize={'xs'}
                                            color={'text.link'}
                                            fontWeight={'medium'}
                                            ml={0.5}
                                        >
                                            {t('4636')}
                                        </Link>{' '}
                                        <Text display={'inline'} textTransform={'lowercase'}>
                                            {t('2504')}
                                        </Text>{' '}
                                        <Link
                                            target={'_blank'}
                                            href={'/cookie-notice'}
                                            variant={'link'}
                                            fontSize={'xs'}
                                            color={'text.link'}
                                            fontWeight={'medium'}
                                            ml={0.5}
                                        >
                                            {t('4637')}
                                        </Link>
                                        .
                                    </Text>
                                </Checkbox>
                                <FormErrorMessage>{errors.terms && errors.terms}</FormErrorMessage>
                            </FormControl>
                        ) : null}

                        {status?.error ? (
                            <Box mb={4}>
                                <FieldError text={status.error} />
                            </Box>
                        ) : null}

                        <FormControl display={'flex'} justifyContent={'space-between'}>
                            <AppButton
                                translate="no"
                                title={t('337')}
                                color={'#2F80ED'}
                                border={'1px solid #2F80ED'}
                                background={'transparent'}
                                w={'120px'}
                                h={'44px'}
                                type={'button'}
                                onClick={() => {
                                    dispatch(removeStepCount())
                                    dispatch(nextStep('createProfile'))
                                }}
                            />
                            <AppButton
                                translate="no"
                                title={t('3035')}
                                type={'submit'}
                                variant={'primary'}
                                isLoading={
                                    isSending ||
                                    isFirstStepLoading ||
                                    isSecondStepLoading ||
                                    isThirdStepLoading ||
                                    isSmsLoading ||
                                    isLoading ||
                                    isSubmitting
                                }
                                w={'120px'}
                                h={'44px'}
                            />
                        </FormControl>
                    </Form>
                )}
            </Formik>
        </Flex>
    )
}
